/**
 * Panelets konfig kommer ved BYGG som `VITE_INNSPEL_*` (tech-decisions.md, «M2.1»).
 * Én deployment for alle leietakere — leietakeren kommer fra tokenet, aldri herfra.
 * Ingen verdi er hemmelig: anon-nøkkelen er Supabases offentlige klientnøkkel.
 * Mangler noe påkrevd, stopper panelet med feltnavnet i meldingen — aldri en
 * stille tom side.
 *
 * Filen har ingen nettleser-API: den leses også av vite.config.ts (Node) for CSP.
 */
export interface PanelKonfig {
  apiUrl: string;
  supabaseUrl: string;
  supabaseAnonKey: string;
  /** Google-knappen på innloggingssiden. Krever provider-oppsett i Supabase. */
  googleLogin: boolean;
  /**
   * Enheten som andre faktor (WebAuthn): Windows Hello, Touch ID, sikkerhetsnøkkel.
   * AV som standard, og det er ikke en smaksak — Supabase støtter ikke faktoren ennå.
   * Management API-et svarer på å skru den på (2026-09-21):
   *
   *   {"message":"Enabling of MFA with WebAuthn not currently supported"}
   *
   * `auth.stoetterEnhet()` spør bare NETTLESEREN, og en Chrome som kan seremonien ville
   * fått «Bruk denne enheten» servert og blitt avvist av serveren hver gang. Flagget er
   * det andre ja-et: nettleseren må kunne det, OG serveren må ha åpnet.
   * Oppskrift og hvordan man ser at det har åpnet: `docs/drift/supabase-enhetsfaktor.md`.
   */
  deviceFactor: boolean;
  /**
   * Opprinnelsen skjermbildene serveres fra — bøttas eget domene (K9). VALGFRI, og det er
   * med vilje: er den ikke satt, står `img-src` uendret og panelet åpner skjermbildet i en
   * ny fane som før. Da blir en usatt variabel en synlig, fungerende flate og ikke et
   * ødelagt bilde.
   *
   * Verdien MÅ være nøyaktig verten i den presignerte URL-en. Er den feil, blokkerer
   * nettleseren bildet, og `Vis bildet` viser en feilmelding med lenke til ny fane.
   */
  attachmentOrigin?: string;
}

type Miljø = Record<string, string | boolean | undefined>;

export function lesKonfig(env: Miljø): PanelKonfig {
  const krev = (navn: string): string => {
    const v = env[navn];
    if (typeof v !== 'string' || v.trim() === '') {
      throw new Error(`[innspel] ${navn} mangler. Panelet bygges med VITE_INNSPEL_*-variabler — se panel/.env.example.`);
    }
    return v.trim();
  };
  const url = (navn: string): string => {
    const v = krev(navn).replace(/\/+$/, '');
    let parsed: URL;
    try {
      parsed = new URL(v);
    } catch {
      throw new Error(`[innspel] ${navn} er ikke en gyldig URL.`);
    }
    // https, eller http kun mot lokal maskin (dev). Tokenet går i Authorization-headeren
    // og skal aldri i klartekst over nettet.
    const lokal = parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1';
    if (parsed.protocol !== 'https:' && !(parsed.protocol === 'http:' && lokal)) {
      throw new Error(`[innspel] ${navn} må være https (http kun mot localhost).`);
    }
    return parsed.origin;
  };
  /** Som `url`, men tom verdi gir undefined i stedet for å kaste. */
  const valgfriUrl = (navn: string): string | undefined => {
    const v = env[navn];
    return typeof v === 'string' && v.trim() !== '' ? url(navn) : undefined;
  };
  const bool = (navn: string): boolean => {
    const v = env[navn];
    return typeof v === 'string' ? v.trim().toLowerCase() === 'true' : v === true;
  };

  const vedleggsOpprinnelse = valgfriUrl('VITE_INNSPEL_ATTACHMENT_ORIGIN');

  return {
    apiUrl: url('VITE_INNSPEL_API_URL'),
    supabaseUrl: url('VITE_INNSPEL_SUPABASE_URL'),
    supabaseAnonKey: krev('VITE_INNSPEL_SUPABASE_ANON_KEY'),
    googleLogin: bool('VITE_INNSPEL_GOOGLE_LOGIN'),
    deviceFactor: bool('VITE_INNSPEL_DEVICE_FACTOR'),
    // Utelates helt når den ikke er satt — `exactOptionalPropertyTypes` skiller
    // «ikke satt» fra «satt til undefined», og CSP-en skal ikke se nøkkelen i det hele tatt.
    ...(vedleggsOpprinnelse ? { attachmentOrigin: vedleggsOpprinnelse } : {}),
  };
}
